/**
 * UserSettingsPage Component
 *
 * User settings page for managing account preferences and security.
 * Features:
 * - User information display
 * - Change password functionality
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { activeLocale } from '@/utils/locale';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { message } from 'antd';
import { UserOutlined, MailOutlined, TeamOutlined, CalendarOutlined, FileTextOutlined, CloudOutlined, StarOutlined, BulbOutlined, SettingOutlined, GlobalOutlined, LogoutOutlined, KeyOutlined } from '@ant-design/icons';
import { Sidebar } from '@/components/layout/Sidebar';
import { getInitials } from '@/utils/getInitials';
import { CloudStorageConnect } from '@modules/data-bundles';
import { MyConnectorsSettings } from '@modules/connector_library/components/MyConnectorsSettings';
import { useAuth } from '@modules/auth';
import authService from '@modules/auth/services/authService';
import { useLanguage } from '@hooks/useLanguage';
import { useTheme } from '@hooks/useTheme';
import { useFeature } from '@/hooks';
import { CostSimulatorPage } from '@modules/cost_simulator';
import AccessTokensPanel from '@modules/auth/components/AccessTokensPanel';
import { personalizationService } from '@modules/conversations/services/personalizationService';
import { forgetDriveConnected } from '@modules/conversations/services/driveService';
import { CloudStorageApi } from '@modules/data-bundles/services/cloudStorageService';
import { claimSameTabOAuthOr, readOAuthCallback } from '@/utils/oauthPopup';
import './UserSettingsPage.css';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';

import { PUBLIC_DOCUMENTS_PUBLISHED } from '@modules/legal/publicPaths';

/** The providers Settings connects in this same tab. */
const CLOUD_PROVIDERS = ['google_drive', 'dropbox'] as const;

export default function UserSettingsPage() {
  // Journey detection: opening your own settings is the Try for the
  // "make it yours" step.
  useEffect(() => {
    emitJourneyEvent(JOURNEY_EVENTS.settingsOpened);
  }, []);
  const { user, logout } = useAuth();
  // Settings now has tabs: General + (for group_admin+) Cost Simulator.
  const [activeTab, setActiveTab] = useState<'general' | 'cost'>('general');
  const costSimulatorEnabled = useFeature('modules.cost_simulator');
  const canUseCost = costSimulatorEnabled && (user?.role === 'group_admin' || user?.role === 'super_admin');
  // Per-tenant gate for the Dropbox / Google Drive connect section below.
  // Driven solely by the "Cloud storage connections" switch — when a super-admin
  // turns integrations.cloud_storage off, the connect surface disappears from
  // User Settings (and the backend routes 403). Mirrors the backend gate.
  const cloudConnectionsEnabled = useFeature('integrations.cloud_storage');
  // Per-tenant gate for the self-service "email me a reset link" button.
  // When the user's own tenant has auth.transactional_email OFF there is no
  // email path — hide the affordance rather than let it 403.
  const transactionalEmailEnabled = useFeature('auth.transactional_email');
  const mcpAccessEnabled = useFeature('modules.mcp_access');
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  // Self-service "reset by email": ~30s client-side cooldown after a send —
  // matches the 3/hour server limit without surfacing 429s for double-clicks.
  const [resetEmailSending, setResetEmailSending] = useState(false);
  const [resetEmailCooldown, setResetEmailCooldown] = useState(false);

  const handleSendResetEmail = async () => {
    setResetEmailSending(true);
    try {
      await authService.sendMyPasswordResetEmail();
      message.success(t('userSettings.resetEmail.success') || 'A password reset link has been sent to your email');
      setResetEmailCooldown(true);
      setTimeout(() => setResetEmailCooldown(false), 30_000);
    } catch (err: any) {
      message.error(err.message || t('userSettings.resetEmail.error'));
    } finally {
      setResetEmailSending(false);
    }
  };

  // Back from the provider in this same tab (Settings → Connect, item 66):
  // the public callback finished nothing — this page finishes it as the
  // signed-in person, and the server refuses a state anyone else started, so
  // a connect link opened by someone else connects nothing (utils/oauthPopup.ts).
  // The provider is the one this tab noted, else the callback's word for it:
  // a tab loaded before item 66 left without noting its state (item 88b).
  const finishing = useRef<string | null>(null);
  const [cloudRefresh, setCloudRefresh] = useState(0);
  useEffect(() => {
    const callback = readOAuthCallback(location.search);
    if (!callback || finishing.current === callback.state) return;
    finishing.current = callback.state;
    const named = new URLSearchParams(location.search).get('provider');
    navigate(location.pathname, { replace: true });   // the code leaves the address bar at once
    const provider = claimSameTabOAuthOr(callback.state, named, CLOUD_PROVIDERS);
    if (!provider) {
      message.error(t('cloudStorage.connectElsewhere'));
      return;
    }
    CloudStorageApi.finishAuthorization(provider, callback)
      .then(({ outcome, detail }) => {
        if (outcome === 'connected') {
          // The composer and artifact panel cache 'is Drive connected?' once per page load.
          forgetDriveConnected();
          setCloudRefresh(n => n + 1);
          message.success(t('admin.cloudConnectedNamed', { provider: provider.replace('_', ' ') }));
        } else if (outcome === 'wrong_account') {
          message.error(t('cloudStorage.access.wrongAccount'));
        } else if (outcome === 'invalid_state') {
          message.error(t('cloudStorage.connectExpired'));
        } else if (outcome !== 'declined') {
          message.error(t('admin.cloudConnectionFailed', { detail: detail?.error || outcome }));
        }
      })
      .catch(() => message.error(t('admin.cloudConnectionFailed', { detail: '' })));
  }, [location.search, navigate, location.pathname]);

  // The callback could not read the state at all: say so once, then clear it.
  useEffect(() => {
    const cloudError = new URLSearchParams(location.search).get('cloud_error');
    if (cloudError) {
      message.error(t('admin.cloudConnectionFailed', { detail: decodeURIComponent(cloudError) }));
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  // Personalization state
  const [personalizationEnabled, setPersonalizationEnabled] = useState(false);
  const [profile, setProfile] = useState('');
  const [originalProfile, setOriginalProfile] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileDetailsOpen, setProfileDetailsOpen] = useState(false);

  // User-provided self-context (role + free-form details).
  // Unlike the AI-managed profile above, the user owns these fields entirely
  // — the AI never writes here, only reads them as ground truth.
  const [selfRole, setSelfRole] = useState('');
  const [originalSelfRole, setOriginalSelfRole] = useState('');
  const [selfDetails, setSelfDetails] = useState('');
  const [originalSelfDetails, setOriginalSelfDetails] = useState('');
  const [selfContextSaving, setSelfContextSaving] = useState(false);

  const isProfileDirty = profile !== originalProfile;
  const isSelfContextDirty =
    selfRole !== originalSelfRole || selfDetails !== originalSelfDetails;

  const loadPersonalization = useCallback(async () => {
    try {
      setProfileLoading(true);
      const data = await personalizationService.getProfile();
      setPersonalizationEnabled(data.enabled);
      setProfile(data.profile || '');
      setOriginalProfile(data.profile || '');
      setSelfRole(data.self_role || '');
      setOriginalSelfRole(data.self_role || '');
      setSelfDetails(data.self_details || '');
      setOriginalSelfDetails(data.self_details || '');
    } catch {
      message.error(t('personalization.loadError') || 'Could not load AI personalization settings');
    } finally {
      setProfileLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPersonalization();
  }, [loadPersonalization]);

  const handleTogglePersonalization = async (enabled: boolean) => {
    try {
      await personalizationService.updateSettings(enabled);
      setPersonalizationEnabled(enabled);
    } catch {
      message.error(t('personalization.toggleError') || 'Could not update personalization setting');
      setPersonalizationEnabled(!enabled);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setProfileSaving(true);
      await personalizationService.updateProfile(profile);
      setOriginalProfile(profile);
      message.success(t('personalization.saveSuccess') || 'Memory profile saved');
    } catch {
      message.error(t('personalization.saveError') || 'Could not save memory profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveSelfContext = async () => {
    try {
      setSelfContextSaving(true);
      await personalizationService.updateSelfContext(selfRole.trim(), selfDetails.trim());
      setOriginalSelfRole(selfRole.trim());
      setOriginalSelfDetails(selfDetails.trim());
      setSelfRole(selfRole.trim());
      setSelfDetails(selfDetails.trim());
      message.success(t('personalization.selfContext.saveSuccess') || 'Your details were saved');
    } catch {
      message.error(t('personalization.selfContext.saveError') || 'Could not save your details');
    } finally {
      setSelfContextSaving(false);
    }
  };

  const handleResetProfile = async () => {
    if (!window.confirm(t('personalization.resetConfirm') || 'Are you sure you want to reset your AI memory profile? This cannot be undone.')) {
      return;
    }
    try {
      await personalizationService.clearProfile();
      setProfile('');
      setOriginalProfile('');
      message.success(t('personalization.resetSuccess') || 'Memory profile cleared');
    } catch {
      message.error(t('personalization.resetError') || 'Could not clear memory profile');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString(activeLocale(), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  // Title-casing the role slug produced an English label no matter the
  // language. The roles.* keys already carry proper Hebrew.
  const formatRole = (role?: string) =>
    t(`roles.${role || 'user'}`, { defaultValue: role || 'user' });

  return (
    <div className="settings-container">
      <Sidebar
        options={{
          showHome: true,
          showChat: false,
          showBots: true,
          showSearch: true,
          showAdmin: true,
          showSuperAdmin: false,
          showConversations: false,
          showBotsList: false,
          activeItem: 'settings',
        }}
      />

      <div className="settings-main">
        {canUseCost && (
          <div className="settings-tabs">
            <button className={`settings-tab-btn ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>{t('userSettings.title')}</button>
            <button className={`settings-tab-btn ${activeTab === 'cost' ? 'active' : ''}`} onClick={() => setActiveTab('cost')}>{t('costSimulator.title')}</button>
          </div>
        )}
        {activeTab === 'general' && (
        <div className="settings-content">
          {/* Page Header */}
          <div className="settings-header">
            <h1 className="settings-title">
              {t('userSettings.title')}
            </h1>
            <p className="settings-subtitle">
              {t('userSettings.subtitle') || 'Manage your account settings'}
            </p>
          </div>

          {/* User Info Card */}
          <div className="settings-section">
            <h2 className="section-title">
              {t('userSettings.accountInfo') || 'Account Information'}
            </h2>
            <div className="user-info-card">
              {/* User Avatar */}
              <div className="user-avatar-large">
                {getInitials(user?.full_name || user?.user_full_name || user?.email || 'U')}
              </div>

              {/* User Details */}
              <div className="user-details">
                <div dir="auto" className="user-name-large">
                  {user?.full_name || user?.user_full_name || user?.email?.split('@')[0] || 'User'}
                </div>

                <div className="user-info-grid">
                  <div className="info-item">
                    <MailOutlined className="info-icon" />
                    <div className="info-content">
                      <span className="info-label">{t('userSettings.email') || 'Email'}</span>
                      <span className="info-value">{user?.email || '-'}</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <TeamOutlined className="info-icon" />
                    <div className="info-content">
                      <span className="info-label">{t('userSettings.role') || 'Role'}</span>
                      <span className="info-value">{formatRole(user?.role)}</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <UserOutlined className="info-icon" />
                    <div className="info-content">
                      <span className="info-label">{t('userSettings.tenant') || 'Organization'}</span>
                      <span className="info-value">{user?.tenant_name || user?.tenant_code || '-'}</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <CalendarOutlined className="info-icon" />
                    <div className="info-content">
                      <span className="info-label">{t('userSettings.memberSince') || 'Member since'}</span>
                      <span className="info-value">{formatDate(user?.created_at)}</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <StarOutlined className="info-icon" />
                    <div className="info-content">
                      <span className="info-label">{t('userSettings.tier') || 'Tier'}</span>
                      <span className="info-value">{user?.user_tier ?? '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preferences Section */}
          <div className="settings-section">
            <h2 className="section-title">
              <SettingOutlined style={{ marginInlineEnd: 8 }} />
              {t('userSettings.preferencesSection') || 'Preferences'}
            </h2>
            <p className="settings-subtitle" style={{ marginBottom: 16 }}>
              {t('userSettings.preferencesDescription') || 'Personalize the look and language of your workspace'}
            </p>
            <div className="security-card">
              {/* Appearance (Dark / Light mode) */}
              <div className="security-item">
                <div className="security-icon">
                  {theme === 'dark' ? (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="4" />
                      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                    </svg>
                  )}
                </div>
                <div className="security-content">
                  <h3 className="security-title">
                    {t('userSettings.appearance') || 'Appearance'}
                  </h3>
                  <p className="security-description">
                    {theme === 'dark'
                      ? (t('theme.dark') || 'Dark Mode')
                      : (t('theme.light') || 'Light Mode')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={theme === 'dark'}
                  className={`toggle-switch ${theme === 'dark' ? 'toggle-on' : ''}`}
                  onClick={toggleTheme}
                  title={theme === 'dark' ? t('theme.light') : t('theme.dark')}
                >
                  <span className="toggle-knob" />
                </button>
              </div>

              {/* Interface Language */}
              <div className="security-item">
                <div className="security-icon">
                  <GlobalOutlined />
                </div>
                <div className="security-content">
                  <h3 className="security-title">
                    {t('userSettings.interfaceLanguage') || 'Interface Language'}
                  </h3>
                  <p className="security-description">
                    {t('userSettings.interfaceLanguageDescription') || 'Choose the language of the interface'}
                  </p>
                </div>
                <div className="language-switch">
                  <button
                    type="button"
                    className={`language-option ${language === 'en' ? 'active' : ''}`}
                    onClick={() => setLanguage('en')}
                    aria-pressed={language === 'en'}
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    className={`language-option ${language === 'he' ? 'active' : ''}`}
                    onClick={() => setLanguage('he')}
                    aria-pressed={language === 'he'}
                  >
                    עב
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Security Section — only when this tenant has transactional email */}
          {transactionalEmailEnabled && (
          <div className="settings-section">
            <h2 className="section-title">
              {t('userSettings.securitySection') || 'Security'}
            </h2>
            <div className="security-card">
              <div className="security-item">
                <div className="security-icon">
                  <MailOutlined />
                </div>
                <div className="security-content">
                  <h3 className="security-title">
                    {t('userSettings.resetEmail.title') || 'Reset password by email'}
                  </h3>
                  <p className="security-description">
                    {t('userSettings.resetEmail.description') || "We'll email you a secure link to set a new password"}
                  </p>
                </div>
                <button
                  className="btn-change-password"
                  onClick={handleSendResetEmail}
                  disabled={resetEmailSending || resetEmailCooldown}
                >
                  {resetEmailSending
                    ? (t('userSettings.resetEmail.sending') || 'Sending…')
                    : resetEmailCooldown
                      ? (t('userSettings.resetEmail.sent') || 'Sent')
                      : (t('userSettings.resetEmail.button') || 'Send reset link')}
                </button>
              </div>
            </div>
          </div>
          )}

          {/* AI Personalization Section */}
          <div className="settings-section">
            <h2 className="section-title">
              <BulbOutlined style={{ marginInlineEnd: 8 }} />
              {t('personalization.title') || 'AI Personalization'}
            </h2>
            <p className="settings-subtitle" style={{ marginBottom: 16 }}>
              {t('personalization.description') || 'Allow the AI to remember your preferences across conversations'}
            </p>
            <div className="security-card">
              <div className="security-item" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 16 }}>
                <div className="personalization-toggle-row">
                  <label className="personalization-toggle">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={personalizationEnabled}
                      className={`toggle-switch ${personalizationEnabled ? 'toggle-on' : ''}`}
                      onClick={() => !profileLoading && handleTogglePersonalization(!personalizationEnabled)}
                      disabled={profileLoading}
                    >
                      <span className="toggle-knob" />
                    </button>
                    <span>{t('personalization.enableToggle') || 'Allow AI to remember things about me'}</span>
                  </label>

                  {personalizationEnabled && !profileLoading && (
                    <button
                      type="button"
                      className="personalization-details-toggle"
                      onClick={() => setProfileDetailsOpen(!profileDetailsOpen)}
                      aria-expanded={profileDetailsOpen}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ transform: profileDetailsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                      {profileDetailsOpen
                        ? (t('personalization.hideDetails') || 'Hide details')
                        : (t('personalization.showDetails') || 'Show details')}
                    </button>
                  )}
                </div>

                {personalizationEnabled && !profileLoading && (
                  <>
                    {profileDetailsOpen && (
                      <>
                        <div className="personalization-profile">
                          <label className="context-label">
                            {t('personalization.selfContext.title') || 'About You'}
                          </label>
                          <p className="settings-subtitle" style={{ margin: 0 }}>
                            {t('personalization.selfContext.description')
                              || 'Tell the AI a bit about yourself. Optional. These fields are read-only for the AI — it cannot edit them.'}
                          </p>
                          <input dir="auto"
                            type="text"
                            className="context-textarea"
                            style={{ minHeight: 0, height: 40 }}
                            value={selfRole}
                            onChange={(e) => setSelfRole(e.target.value)}
                            maxLength={200}
                            placeholder={t('personalization.selfContext.rolePlaceholder')
                              || 'Your role at the company — e.g., Backend engineer, Product manager, Operations lead'}
                            aria-label={t('personalization.selfContext.roleLabel') || 'Your role'}
                          />
                          <textarea dir="auto"
                            className="context-textarea"
                            value={selfDetails}
                            onChange={(e) => setSelfDetails(e.target.value)}
                            rows={5}
                            maxLength={2000}
                            placeholder={t('personalization.selfContext.detailsPlaceholder')
                              || "Anything else you'd like the AI to know upfront — e.g., 'I lead a 6-person ops team. I prefer concise, structured answers. I work in Hebrew but write code in English.'"}
                            aria-label={t('personalization.selfContext.detailsLabel') || 'About you'}
                          />
                          <div className="personalization-actions">
                            <button
                              type="button"
                              className="btn-change-password"
                              onClick={handleSaveSelfContext}
                              disabled={!isSelfContextDirty || selfContextSaving}
                            >
                              {selfContextSaving
                                ? (t('common.saving') || 'Saving...')
                                : (t('personalization.selfContext.saveButton') || 'Save your details')}
                            </button>
                          </div>
                        </div>
                        <div className="personalization-profile">
                          <label className="context-label">
                            {t('personalization.profileLabel') || 'Memory Profile'}
                          </label>
                          {profile ? (
                            <textarea dir="auto"
                              className="context-textarea"
                              value={profile}
                              onChange={(e) => setProfile(e.target.value)}
                              rows={10}
                              placeholder={t('personalization.emptyState') || 'The AI will build your profile as you chat...'}
                            />
                          ) : (
                            <p className="personalization-empty">
                              {t('personalization.emptyState') || 'No profile yet. The AI will start learning your preferences as you chat.'}
                            </p>
                          )}
                        </div>
                        <div className="personalization-actions">
                          {profile && (
                            <>
                              <button
                                className="btn-change-password"
                                onClick={handleSaveProfile}
                                disabled={!isProfileDirty || profileSaving}
                              >
                                {profileSaving
                                  ? (t('common.saving') || 'Saving...')
                                  : (t('common.save') || 'Save')}
                              </button>
                              <button
                                className="btn-reset-profile"
                                onClick={handleResetProfile}
                              >
                                {t('personalization.resetButton') || 'Reset Profile'}
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Cloud Storage Section — gated per-tenant by super-admin */}
          {cloudConnectionsEnabled && (
          <div className="settings-section">
            <h2 className="section-title">
              <CloudOutlined style={{ marginInlineEnd: 8 }} />
              {t('userSettings.cloudStorage') || 'Cloud Storage'}
            </h2>
            <p className="settings-subtitle" style={{ marginBottom: 16 }}>
              {t('userSettings.cloudStorageDescription') || 'Connect your cloud accounts to import your files'}
            </p>
            <CloudStorageConnect refreshKey={cloudRefresh} />
          </div>
          )}

          {/* Connectors each person signs in to with their own account —
              present only once a manager chose that for one of this
              person's connectors. */}
          <MyConnectorsSettings />

          {/* Access Tokens — gated per-tenant on modules.mcp_access */}
          {mcpAccessEnabled && (
          <div className="settings-section">
            <h2 className="section-title">
              <KeyOutlined style={{ marginInlineEnd: 8 }} />
              {t('accessTokens.sectionTitle') || 'Access tokens'}
            </h2>
            <AccessTokensPanel />
          </div>
          )}

          {/* Legal Section — while the public documents are unpublished, the
              original Terms of Service and Privacy Policy, signed-in only */}
          {!PUBLIC_DOCUMENTS_PUBLISHED && (
          <div className="settings-section">
            <h2 className="section-title">
              {t('userSettings.legalSection') || 'Legal'}
            </h2>
            <div className="security-card">
              <Link to={tenantPath('/settings/terms')} className="security-item legal-link">
                <div className="security-icon">
                  <FileTextOutlined />
                </div>
                <div className="security-content">
                  <h3 className="security-title">
                    {t('userSettings.termsAndPrivacy') || 'Terms of Service and Privacy Policy'}
                  </h3>
                  <p className="security-description">
                    {t('userSettings.termsAndPrivacyDescription') || 'Read terms of service and privacy policy'}
                  </p>
                </div>
              </Link>
            </div>
          </div>
          )}

          {/* Legal Section — once the public documents are published */}
          {PUBLIC_DOCUMENTS_PUBLISHED && (
          <div className="settings-section">
            <h2 className="section-title">
              {t('userSettings.legalSection') || 'Legal'}
            </h2>
            <div className="security-card">
              <a href="/privacy" className="security-item legal-link">
                <div className="security-icon">
                  <FileTextOutlined />
                </div>
                <div className="security-content">
                  <h3 className="security-title">{t('legal.nav.privacy')}</h3>
                  <p className="security-description">{t('userSettings.privacyDescription')}</p>
                </div>
              </a>
              <a href="/terms" className="security-item legal-link">
                <div className="security-icon">
                  <FileTextOutlined />
                </div>
                <div className="security-content">
                  <h3 className="security-title">{t('legal.nav.terms')}</h3>
                  <p className="security-description">{t('userSettings.termsDescription')}</p>
                </div>
              </a>
            </div>
          </div>
          )}

          {/* Log Out — at the bottom of the page */}
          <div className="settings-section">
            <button
              type="button"
              className="settings-logout-btn"
              onClick={logout}
            >
              <LogoutOutlined className="settings-logout-icon rtl-flip" />
              <span>{t('nav.logout') || 'Log Out'}</span>
            </button>
          </div>
        </div>
        )}
        {activeTab === 'cost' && canUseCost && (
          <div className="settings-cost-tab">
            <CostSimulatorPage embedded />
          </div>
        )}
      </div>
    </div>
  );
}
